import { groupBy, mapValues, uniq } from "lodash";
import type {
  Offering,
  OfferingCategory,
} from "src/generated/hooks/form-service/requests";
import type { Group, Item } from "src/types/groupedItems";

export type OfferingOption = {
  id: string;
  label: string;
  categoryId: string;
  categoryLabel: string;
};

export type SeatType = {
  id: string;
  option: OfferingOption | null;
};

export type SchoolOffering = {
  id: string;
  options: OfferingOption[];
  seatTypes: SeatType[];
};

export const GENERAL_SEAT_TYPE_LABEL = "General";
export const GENERAL_SEAT_TYPE_OPTION_KEY = "general";

export const MAX_OPTIONS_IN_OFFERING = 2;

export function flattenOfferingOptions(
  categories: OfferingCategory[]
): OfferingOption[] {
  return categories.flatMap((category) =>
    category.options.map((option) => ({
      id: option.id,
      label: option.label,
      categoryId: category.id,
      categoryLabel: category.label,
    }))
  );
}

export function groupOfferingOptionsByCategory(
  options: OfferingOption[]
): Group<Item>[] {
  const optionsByCategoryId = groupBy(options, "categoryId");
  const categoryIds = uniq(options.map((option) => option.categoryId));
  return categoryIds.map((categoryId) => {
    const optionsInCategory = optionsByCategoryId[categoryId] ?? [];
    return {
      group: optionsInCategory[0]?.categoryLabel ?? null,
      items: optionsInCategory.map((option) => ({
        key: option.id,
        label: option.label,
      })),
    };
  });
}

export function getAvailableOfferingOptions({
  options,
  selectedOptionIds,
  slotIndex,
  totalNumSlots,
  existingOfferingKeys,
}: {
  options: OfferingOption[];
  selectedOptionIds: Array<string | null>;
  slotIndex: number;
  totalNumSlots: number;
  existingOfferingKeys: Set<string>;
}): OfferingOption[] {
  const selectedInOtherSlots = selectedOptionIds.filter(
    (optionId, index) => index !== slotIndex && optionId !== null
  );

  // Don't allow the same category to be selected more than once in the same offering
  const takenCategoryIds = new Set(
    options
      .filter((option) => selectedInOtherSlots.includes(option.id))
      .map((option) => option.categoryId)
  );
  const availableOptions = options.filter((option) => {
    return !takenCategoryIds.has(option.categoryId);
  });

  // For the last slot, prevent selecting an option that would create a duplicate offering
  const isLastSlot = slotIndex === totalNumSlots - 1;
  const precedingOptionIds = selectedOptionIds.slice(0, totalNumSlots - 1);
  if (isLastSlot && precedingOptionIds.every((optionId) => optionId !== null)) {
    return availableOptions.filter((option) => {
      return !existingOfferingKeys.has(
        offeringKey([...precedingOptionIds, option.id])
      );
    });
  }

  return availableOptions;
}

export function offeringKey(optionIds: string[]): string {
  return JSON.stringify(optionIds);
}

export function seatTypeLabel(seatType: SeatType): string {
  return seatType.option?.label ?? GENERAL_SEAT_TYPE_LABEL;
}

export function hasGeneralSeatType(seatTypes: SeatType[]): boolean {
  return seatTypes.some((seatType) => seatType.option === null);
}

export function getAvailableSeatTypeOptions({
  options,
  offeringOptionIds,
  existingSeatTypeOptionIds,
}: {
  options: OfferingOption[];
  offeringOptionIds: string[];
  existingSeatTypeOptionIds: string[];
}): OfferingOption[] {
  const takenSeatTypeOptionIds = new Set(existingSeatTypeOptionIds);
  const takenCategoryIds = new Set(
    options
      .filter((option) => offeringOptionIds.includes(option.id))
      .map((option) => option.categoryId)
  );

  return options.filter(
    (option) =>
      !takenSeatTypeOptionIds.has(option.id) &&
      !takenCategoryIds.has(option.categoryId)
  );
}

export function toSchoolOffering(
  offering: Offering,
  optionById: Map<string, OfferingOption>
): SchoolOffering {
  return {
    id: offering.id,
    options: offering.offeringOptionIds.map((optionId) => {
      const option = optionById.get(optionId);
      if (!option) {
        throw new Error(`Offering option ${optionId} not found`);
      }
      return option;
    }),
    seatTypes: offering.seatTypes.map((seatType) => {
      const optionId = seatType.offeringOptionIds[0];
      if (optionId === undefined) {
        return { id: seatType.id, option: null };
      }
      const option = optionById.get(optionId);
      if (!option) {
        throw new Error(`Offering option ${optionId} not found`);
      }
      return { id: seatType.id, option };
    }),
  };
}

export function groupOfferingsBySchoolId(
  offerings: Offering[],
  options: OfferingOption[]
): Record<string, SchoolOffering[]> {
  const optionById = new Map(options.map((option) => [option.id, option]));
  const offeringsGroupedBySchool = groupBy(offerings, "schoolId");
  return mapValues(offeringsGroupedBySchool, (schoolOfferings) =>
    schoolOfferings
      .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt))
      .map((offering) => toSchoolOffering(offering, optionById))
  );
}
